import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import useMediaQuery from "../../hooks/useMediaQuery";
import posts from "../../data/posts.json";
import NotFound from "./NotFound";

const revealVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: 0.1 * i },
  }),
};

const BackLink = ({ className }) => (
  <Link
    to="/blog"
    className={`font-satoshi font-normal text-clay-2 transition-colors
    duration-300 hover:text-off-white ${className}`}
  >
    &larr; All posts
  </Link>
);

const BlogPost = () => {
  const { slug } = useParams();
  const aboveMediumScreens = useMediaQuery("(min-width: 1061px)");
  const post = posts.find((entry) => entry.slug === slug);

  if (!post) {
    return (
      <NotFound
        title="Post not found"
        message="That post has either moved or was never published."
      />
    );
  }

  const header = (
    <>
      <motion.div variants={revealVariants} initial="hidden" animate="visible">
        <BackLink className={aboveMediumScreens ? "text-base" : "text-sm"} />
      </motion.div>
      <motion.p
        variants={revealVariants}
        initial="hidden"
        animate="visible"
        custom={1}
        className={`mt-8 font-satoshi font-normal text-clay-2 ${
          aboveMediumScreens ? "text-base" : "text-sm"
        }`}
      >
        {post.dateLabel}
      </motion.p>
      <motion.h1
        variants={revealVariants}
        initial="hidden"
        animate="visible"
        custom={2}
        className={`mt-3 font-generalsans font-extrabold leading-[1.05] ${
          aboveMediumScreens ? "text-7xl" : "text-4xl"
        }`}
      >
        {post.title}
      </motion.h1>
      {post.cover && (
        <motion.img
          variants={revealVariants}
          initial="hidden"
          animate="visible"
          custom={3}
          src={post.cover}
          alt={post.title}
          className="mt-10 w-full rounded-2xl object-cover"
        />
      )}
    </>
  );

  const body = (
    <motion.div
      variants={revealVariants}
      initial="hidden"
      animate="visible"
      custom={3}
      className={`post-body font-satoshi ${
        aboveMediumScreens ? "mt-14 text-lg" : "mt-10 text-base"
      }`}
      // Post HTML is generated at build time from markdown this repo owns.
      dangerouslySetInnerHTML={{ __html: post.html }}
    />
  );

  return (
    <>
      {aboveMediumScreens ? (
        <section
          className="min-h-screen mt-[72px] pb-32 relative overflow-hidden
          bg-off-black pt-16 text-off-white"
        >
          <article className="mx-auto w-full max-w-[760px] px-[7%] lg:px-0">
            {header}
            {body}
            <div className="mt-20 border-t border-semi-black pt-10">
              <BackLink className="text-base" />
            </div>
          </article>
        </section>
      ) : (
        /* MOBILE */
        <section
          className="min-h-screen mt-[41px] pb-24 relative overflow-hidden
          bg-off-black pt-10 text-off-white"
        >
          <article className="px-6">
            {header}
            {body}
            <div className="mt-14 border-t border-semi-black pt-8">
              <BackLink className="text-sm" />
            </div>
          </article>
        </section>
      )}
    </>
  );
};

export default BlogPost;
